/**
 * useAudioMixer — Audio mixing hook for the audio remix modal.
 *
 * Original video audio plays through the native <video> element.
 * Ducking is done via video.volume (no crossOrigin needed).
 * TTS clips play through Web Audio API AudioBufferSourceNodes.
 *
 * 15ms crossfades prevent pops at transitions.
 */

import { useCallback, useEffect, useRef } from 'react'
import { fetchResolvedMediaBlob, createNadDebug } from '@ponpon/api-client'
const mixerLog = createNadDebug('dw:audio-remix:mixer')

const FADE_S = 0.015 // 15ms crossfade

export interface MixerClip {
  id: string
  url: string
  startS: number // position on video timeline
  endS: number
  enabled: boolean
  sourceDurationS?: number // actual TTS clip duration (may differ from endS - startS)
  duck?: boolean // whether to duck video audio when this clip plays (default: true)
}

interface ActiveSource {
  source: AudioBufferSourceNode
  clipId: string
}

export function useAudioMixer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
) {
  const ctxRef = useRef<AudioContext | null>(null)
  const clipGainsRef = useRef<Map<string, GainNode>>(new Map())
  const buffersRef = useRef<Map<string, AudioBuffer>>(new Map())
  const bufferUrlsRef = useRef<Map<string, string>>(new Map())
  const decodingRef = useRef<Set<string>>(new Set())
  const activeSourcesRef = useRef<Map<string, ActiveSource>>(new Map())
  const clipsRef = useRef<MixerClip[]>([])
  const rafRef = useRef<number>(0)
  const initializedRef = useRef(false)
  const lastOverlapLogKeyRef = useRef('')
  const lastSpeedLogKeyRef = useRef('')

  // ── Initialize AudioContext ──
  const init = useCallback(() => {
    if (initializedRef.current) return

    const ctx = new AudioContext()
    ctxRef.current = ctx
    initializedRef.current = true
    mixerLog('init: AudioContext created sampleRate=%d', ctx.sampleRate)
  }, [])

  // ── Decode a clip URL into an AudioBuffer ──
  const decodeClip = useCallback(async (id: string, url: string) => {
    const ctx = ctxRef.current
    if (!ctx || decodingRef.current.has(id)) return
    if (buffersRef.current.has(id) && bufferUrlsRef.current.get(id) === url) return

    try {
      decodingRef.current.add(id)
      mixerLog('clip-decode-start id=%s url=%s', id, url)
      const resp = await fetchResolvedMediaBlob(url)
      if (!resp.ok) {
        mixerLog('clip-decode-fetch-fail id=%s status=%d', id, resp.status)
        return
      }
      const arrayBuf = await resp.arrayBuffer()
      mixerLog('clip-decode-fetched id=%s bytes=%d', id, arrayBuf.byteLength)
      const audioBuf = await ctx.decodeAudioData(arrayBuf)
      buffersRef.current.set(id, audioBuf)
      bufferUrlsRef.current.set(id, url)
      mixerLog('clip-decode-done id=%s duration=%.2fs', id, audioBuf.duration)

      if (!clipGainsRef.current.has(id)) {
        const gain = ctx.createGain()
        gain.gain.value = 0
        gain.connect(ctx.destination)
        clipGainsRef.current.set(id, gain)
      }
    } catch (e) {
      mixerLog('clip-decode-error id=%s %s', id, String(e))
    } finally {
      decodingRef.current.delete(id)
    }
  }, [])

  // ── Update the clips list (call on every highlight/position change) ──
  const updateClips = useCallback((clips: MixerClip[]) => {
    clipsRef.current = clips
    if (ctxRef.current) {
      for (const clip of clips) {
        if (clip.enabled && clip.url) {
          void decodeClip(clip.id, clip.url)
        }
      }
    }
  }, [decodeClip])

  const stopActiveSources = useCallback(() => {
    activeSourcesRef.current.forEach(({ source }) => {
      try { source.stop() } catch { /* already stopped */ }
    })
    activeSourcesRef.current.clear()
  }, [])

  // ── The sync loop ──
  const syncLoop = useCallback(() => {
    const ctx = ctxRef.current
    const video = videoRef.current
    if (!ctx || !video) {
      rafRef.current = requestAnimationFrame(syncLoop)
      return
    }

    if (video.paused) {
      rafRef.current = requestAnimationFrame(syncLoop)
      return
    }

    if (ctx.state === 'suspended') {
      ctx.resume()
    }

    const now = ctx.currentTime
    const t = video.currentTime
    const clips = clipsRef.current

    // Determine which clips should be active
    const activeClipIds = new Set<string>()
    let shouldDuck = false

    for (const clip of clips) {
      if (!clip.enabled) continue
      if (!buffersRef.current.has(clip.id)) continue
      if (t >= clip.startS && t < clip.endS) {
        activeClipIds.add(clip.id)
        if (clip.duck !== false) {
          shouldDuck = true
        }
      }
    }

    // Duck original video audio via volume property
    const targetVol = shouldDuck ? 0.0 : 1.0
    if (Math.abs(video.volume - targetVol) > 0.01) {
      video.volume = targetVol
    }
    if (activeClipIds.size > 0 && !video.muted && video.volume > 0.01) {
      const activeClipKey = Array.from(activeClipIds).join('|')
      const logKey = `${Math.floor(t * 2) / 2}:${activeClipKey}:${video.volume.toFixed(2)}`
      if (logKey !== lastOverlapLogKeyRef.current) {
        lastOverlapLogKeyRef.current = logKey
        mixerLog({
          event: 'audio-remix-original-overlap',
          timeS: Number(t.toFixed(3)),
          activeClipIds: activeClipKey,
          activeClipCount: activeClipIds.size,
          videoVolume: Number(video.volume.toFixed(3)),
          videoMuted: video.muted,
          shouldDuck,
        })
      }
    }

    // Start/stop clip sources
    for (const clip of clips) {
      const buffer = buffersRef.current.get(clip.id)
      const clipGain = clipGainsRef.current.get(clip.id)
      if (!buffer || !clipGain) continue

      if (activeClipIds.has(clip.id)) {
        // Should be playing
        if (!activeSourcesRef.current.has(clip.id)) {
          // Start new source at correct offset
          const windowDur = Math.max(clip.endS - clip.startS, 0.001)
          const srcDur = clip.sourceDurationS ?? buffer.duration
          const speed = srcDur / windowDur
          const offset = Math.max(0, (t - clip.startS) * speed)
          const remaining = Math.min(srcDur - offset, (clip.endS - t) * speed)
          if (offset >= buffer.duration || remaining <= 0) {
            continue
          }

          const source = ctx.createBufferSource()
          source.buffer = buffer
          source.playbackRate.value = speed
          source.connect(clipGain)

          if (Math.abs(speed - 1) > 0.01) {
            const logKey = `${clip.id}:${speed.toFixed(3)}:${clip.startS.toFixed(3)}:${clip.endS.toFixed(3)}`
            if (logKey !== lastSpeedLogKeyRef.current) {
              lastSpeedLogKeyRef.current = logKey
              mixerLog({
                event: 'audio-remix-preview-speed-change',
                clipId: clip.id,
                sourceDurationS: Number(srcDur.toFixed(3)),
                windowDurationS: Number(windowDur.toFixed(3)),
                speedRatio: Number(speed.toFixed(4)),
                previewEngine: 'AudioBufferSourceNode.playbackRate',
                pitchPreserving: false,
              })
            }
          }

          mixerLog(
            'clip-start: id=%s srcDur=%.2f windowDur=%.2f speed=%.3f offset=%.3f remaining=%.3f',
            clip.id, srcDur, windowDur, speed, offset, remaining,
          )

          // Fade in
          clipGain.gain.cancelScheduledValues(now)
          clipGain.gain.setValueAtTime(0, now)
          clipGain.gain.linearRampToValueAtTime(1.0, now + FADE_S)

          try {
            source.start(0, offset, Math.max(0, remaining) + (FADE_S * speed))
          } catch (e) {
            mixerLog('clip-start-error: id=%s %s', clip.id, String(e))
            continue
          }

          source.onended = () => {
            activeSourcesRef.current.delete(clip.id)
            mixerLog('clip-end: id=%s', clip.id)
            if (clipGain) {
              clipGain.gain.cancelScheduledValues(ctx.currentTime)
              clipGain.gain.setValueAtTime(clipGain.gain.value, ctx.currentTime)
              clipGain.gain.linearRampToValueAtTime(0, ctx.currentTime + FADE_S)
            }
          }

          activeSourcesRef.current.set(clip.id, { source, clipId: clip.id })
        }
      } else {
        // Should not be playing — stop if active
        const active = activeSourcesRef.current.get(clip.id)
        if (active) {
          // Fade out then stop
          mixerLog('clip-stop: id=%s t=%.3f', clip.id, t)
          clipGain.gain.cancelScheduledValues(now)
          clipGain.gain.setValueAtTime(clipGain.gain.value, now)
          clipGain.gain.linearRampToValueAtTime(0, now + FADE_S)
          try { active.source.stop(now + FADE_S) } catch { /* already stopped */ }
          activeSourcesRef.current.delete(clip.id)
        }
      }
    }

    rafRef.current = requestAnimationFrame(syncLoop)
  }, [videoRef])

  // ── Video event handlers ──
  const handlePlay = useCallback(() => {
    if (!ctxRef.current) init()
    const ctx = ctxRef.current
    if (ctx?.state === 'suspended') ctx.resume()
    if (ctx) {
      for (const clip of clipsRef.current) {
        if (clip.enabled && clip.url) {
          void decodeClip(clip.id, clip.url)
        }
      }
    }
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(syncLoop)
  }, [decodeClip, init, syncLoop])

  const handlePause = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    stopActiveSources()
    // Restore video volume when paused
    const video = videoRef.current
    if (video) video.volume = 1.0
  }, [stopActiveSources, videoRef])

  const handleSeeked = useCallback(() => {
    // AudioBufferSourceNode is one-shot — must recreate after seek
    stopActiveSources()
  }, [stopActiveSources])

  // ── Wire video events ──
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('seeked', handleSeeked)

    // Start RAF for playhead tracking even when paused
    rafRef.current = requestAnimationFrame(syncLoop)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('seeked', handleSeeked)
      cancelAnimationFrame(rafRef.current)
    }
  }, [videoRef, handlePlay, handlePause, handleSeeked, syncLoop])

  // ── Cleanup ──
  useEffect(() => {
    const video = videoRef.current
    return () => {
      cancelAnimationFrame(rafRef.current)
      stopActiveSources()
      if (video) video.volume = 1.0
      ctxRef.current?.close()
    }
  }, [stopActiveSources, videoRef])

  return {
    init,
    decodeClip,
    updateClips,
    getPlayheadTime: () => videoRef.current?.currentTime ?? 0,
  }
}
